import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const LegendDriverIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5-8.5l1.96 2.5H17V9.5h2.5zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const LegendBookingIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const LegendPushpinIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M16 9V4.5C16 3.12 14.88 2 13.5 2h-3C9.12 2 8 3.12 8 4.5V9c-2.21 0-4 1.79-4 4s1.79 4 4 4h3v4h2v-4h3c2.21 0 4-1.79 4-4s-1.79-4-4-4z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const panelStyles = {
    container: {
        position: 'absolute',
        top: '12px',
        left: '12px',
        zIndex: 99,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    toggleBtn: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        border: 'none',
        background: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #333)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        transition: 'background 0.15s',
    },
    panel: {
        position: 'absolute',
        top: '48px',
        left: 0,
        background: 'var(--bs-body-bg, #fff)',
        color: 'var(--bs-body-color, #212529)',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        padding: '14px 16px',
        minWidth: '220px',
        maxHeight: 'min(400px, 60vh)',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        msOverflowStyle: 'none',
        zIndex: 10002,
    },
    title: {
        fontSize: '12px',
        fontWeight: 700,
        color: 'var(--bs-secondary-color, #555)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '12px',
    },
    legendSection: {
        marginBottom: '12px',
    },
    sectionTitle: {
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#6b7280',
        marginBottom: '8px',
    },
    legendRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '13px',
        color: 'var(--bs-body-color, #333)',
    },
    divider: {
        height: '1px',
        background: 'var(--bs-border-color, #f0f0f0)',
        margin: '8px 0',
    },
};

const MapLegendPanel = () => {
    const [expanded, setExpanded] = useState(false);
    const anchorRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!expanded) return;
        const handleMouseDown = (e) => {
            if (
                anchorRef.current?.contains(e.target) ||
                panelRef.current?.contains(e.target)
            ) return;
            setExpanded(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [expanded]);

    return (
        <div style={panelStyles.container} ref={anchorRef}>
            <button
                type="button"
                style={{
                    ...panelStyles.toggleBtn,
                    background: expanded ? '#4285F4' : 'var(--bs-body-bg, #fff)',
                    color: expanded ? '#fff' : 'var(--bs-body-color, #333)',
                }}
                onClick={() => setExpanded((prev) => !prev)}
                title="Map Legend"
            >
                <span role="img" aria-label="legend">🗺️</span>
            </button>
            {expanded && (
                <div
                    ref={panelRef}
                    style={panelStyles.panel}
                    role="dialog"
                    aria-label="Map Legend"
                >
                    <div style={panelStyles.title}>Map Legend</div>

                    {/* Drivers Section */}
                    <div style={panelStyles.legendSection}>
                        <div style={panelStyles.sectionTitle}>🚛 Drivers</div>
                        <div style={panelStyles.legendRow}>
                            <div style={panelStyles.legendItem}>
                                <LegendDriverIcon color="#10B981" />
                                <span>Available</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendDriverIcon color="#FFFFFF" />
                                <span>Accepted</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendDriverIcon color="#3B82F6" />
                                <span>On Trip</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendDriverIcon color="#F59E0B" />
                                <span>Inactive</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendDriverIcon color="#9CA3AF" />
                                <span>Registered</span>
                            </div>
                        </div>
                    </div>

                    <div style={panelStyles.divider} />

                    {/* Bookings Section */}
                    <div style={panelStyles.legendSection}>
                        <div style={panelStyles.sectionTitle}>📦 Bookings</div>
                        <div style={panelStyles.legendRow}>
                            <div style={panelStyles.legendItem}>
                                <LegendBookingIcon color="#EF4444" />
                                <span>Unassigned</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendBookingIcon color="#A855F7" />
                                <span>Delayed</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendBookingIcon color="#FFFFFF" />
                                <span>Pending</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendBookingIcon color="#3B82F6" />
                                <span>Assigned</span>
                            </div>
                            <div style={panelStyles.legendItem}>
                                <LegendBookingIcon color="#10B981" />
                                <span>Completed</span>
                            </div>
                        </div>
                    </div>

                    <div style={panelStyles.divider} />

                    {/* Drop Point */}
                    <div style={{ ...panelStyles.legendSection, marginBottom: 0 }}>
                        <div style={panelStyles.legendRow}>
                            <div style={panelStyles.legendItem}>
                                <LegendPushpinIcon color="#EF4444" />
                                <span style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Drop Point (Select booking to see)</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapLegendPanel;
